import React, { useState, useMemo, useEffect } from 'react';
import { get, round } from 'lodash';
import Button from '@tds/core-button';
import { Formik, Form, Field } from 'formik';
import { Delete, IconButton } from '@tds/core-interactive-icon';
import PropTypes from 'prop-types';

import Modal from '../../../design/shared/modal';

import {
  customStyles,
  ModalFieldLabel,
  ModalFooter,
  Table,
  Tr,
  Td,
  Th,
  TdImp,
  TdLabelR
} from './css';
import { formatCurrency } from '../../../utils/currencyFormatter';

const PricingAndEstimates = ({
  onChange,
  defaultValues,
  orderData,
  isEditable = true
}) => {
  const [isModalVisible, setModalVisible] = useState(false);

  const [
    pricingAndEstimatesSelection,
    setPricingAndEstimatesSlection
  ] = useState([]);
  const [errors, setErrors] = useState();

  useEffect(() => {
    setPricingAndEstimatesSlection(get(defaultValues, 'selections', []));
  }, [orderData]);

  const totals = useMemo(() => {
    let totalCostSum = 0;
    let totalPriceSum = 0;
    pricingAndEstimatesSelection.forEach(item => {
      totalCostSum += round(item.totalCost, 2);
      totalPriceSum += round(item.totalPrice, 2);
    });
    return {
      totalCost: totalCostSum,
      totalPrice: totalPriceSum
    };
  }, [pricingAndEstimatesSelection]);

  const redeemOfferAdjustedTotalPrice = useMemo(() => {
    let monthlyPrice = 0;
    let oneTimePrice = 0;
    let currency = '';

    pricingAndEstimatesSelection.forEach(item => {
      if (item.chargeType === 'One Time') {
        oneTimePrice += round(item.totalPrice, 2);
      } else {
        monthlyPrice += round(item.totalPrice, 2);
      }
      currency = item.currency;
    });
    return {
      monthlyPrice,
      oneTimePrice,
      currency
    };
  }, [pricingAndEstimatesSelection]);

  const showModal = () => {
    setErrors({});
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setModalVisible(false);
  };

  const addLicencingRow = row => {
    if (row.quantity <= 0 || row.price < 0 || row.cost < 0) {
      setErrors(prevErrors => ({
        ...prevErrors,
        quantity: row.quantity <= 0 ? 'Quantity must be greater than 0' : '',
        cost: row.cost < 0 ? 'Cost must be equal and greater than 0' : '',
        price: row.price < 0 ? 'Price must be equal and greater than O' : ''
      }));
      return;
    }
    onCloseModal();
    setPricingAndEstimatesSlection([...pricingAndEstimatesSelection, row]);
  };

  const handleWheel = e => e.target.blur();

  const deleteItem = (e, idx) => {
    e.preventDefault();
    setPricingAndEstimatesSlection([
      ...pricingAndEstimatesSelection.slice(0, idx),
      ...pricingAndEstimatesSelection.slice(idx + 1)
    ]);
  };

  useEffect(() => {
    onChange({
      selections: pricingAndEstimatesSelection,
      redeemOfferAdjustedTotalPrice
    });
  }, [pricingAndEstimatesSelection, redeemOfferAdjustedTotalPrice]);
  return (
    <div>
      {!!pricingAndEstimatesSelection.length && (
        <Table>
          <Th>Item</Th>
          <Th>Description</Th>
          <Th>Currency</Th>
          <Th>Charge Type</Th>
          <Th>Quantity</Th>
          <Th>Unit Cost</Th>
          <Th>Unit Price</Th>
          <Th>Total Cost</Th>
          <Th>Total Price</Th>
          {isEditable && <Th></Th>}
          {pricingAndEstimatesSelection.map(
            (
              {
                item,
                description,
                currency,
                chargeType,
                quantity,
                cost,
                price,
                totalCost,
                totalPrice
              },
              idx
            ) => (
              <Tr>
                <Td>{item}</Td>
                <Td>{description}</Td>
                <Td>{currency}</Td>
                <Td>{chargeType}</Td>
                <Td>{quantity}</Td>
                <Td>${cost}</Td>
                <Td>${price}</Td>
                <Td>{formatCurrency(totalCost)}</Td>
                <Td>{formatCurrency(totalPrice)}</Td>
                {isEditable && (
                  <Td>
                    <IconButton
                      icon={Delete}
                      onClick={e => deleteItem(e, idx)}
                    />
                  </Td>
                )}
              </Tr>
            )
          )}
          <Tr>
            <TdLabelR colSpan={7}>Total</TdLabelR>
            <TdImp>{formatCurrency(totals.totalCost)}</TdImp>
            <TdImp>{formatCurrency(totals.totalPrice)}</TdImp>
          </Tr>
        </Table>
      )}
      {isEditable && <Button onClick={showModal}>Add Line Item</Button>}
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        customStyles={customStyles}
        header="Items Details"
      >
        <Formik
          initialValues={{
            item: '',
            description: '',
            currency: '',
            chargeType: '',
            cost: 0,
            price: 0,
            quantity: 0,
            totalCost: 0,
            totalPrice: 0
          }}
          onSubmit={values => {
            addLicencingRow(values);
          }}
        >
          {({ values, setFieldValue }) => {
            useEffect(() => {
              const totalCost = round(values.cost * values.quantity, 2);
              const totalPrice = round(values.price * values.quantity, 2);
              setFieldValue('totalCost', totalCost);
              setFieldValue('totalPrice', totalPrice);
              if (pricingAndEstimatesSelection.length > 0) {
                setFieldValue(
                  'currency',
                  pricingAndEstimatesSelection[0].currency
                );
              }
            }, [values.cost, values.quantity, values.price, setFieldValue]);
            return (
              <Form>
                <div>
                  <ModalFieldLabel htmlFor="qtyOfLicenses">
                    Item:
                  </ModalFieldLabel>
                  <Field
                    type="text"
                    id="item"
                    name="item"
                    className="frg-input-field color-light"
                    onWheel={handleWheel}
                    onChange={e => setFieldValue('item', e.target.value)}
                    style={{ width: '100%' }}
                    required
                  />
                </div>
                <div>
                  <ModalFieldLabel htmlFor="qtyOfLicenses">
                    Description:
                  </ModalFieldLabel>
                  <Field
                    type="text"
                    id="description"
                    name="description"
                    className="frg-input-field color-light"
                    onWheel={handleWheel}
                    onChange={e => setFieldValue('description', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <ModalFieldLabel>Currency</ModalFieldLabel>
                  <Field
                    as="select"
                    id="currency"
                    name="currency"
                    className="frg-input-field color-light"
                    onChange={e => setFieldValue('currency', e.target.value)}
                    disabled={pricingAndEstimatesSelection.length > 0}
                    required
                  >
                    <option value="">Select Currency</option>
                    <option key="USD" value="USD">
                      USD
                    </option>
                    <option key="CAD" value="CAD">
                      CAD
                    </option>
                  </Field>
                </div>
                <div>
                  <ModalFieldLabel>Charge Type</ModalFieldLabel>
                  <Field
                    as="select"
                    id="chargeType"
                    name="chargeType"
                    className="frg-input-field color-light"
                    onChange={e => setFieldValue('chargeType', e.target.value)}
                    required
                  >
                    <option value="">Select Charge Type</option>
                    <option key="One Time" value="One Time">
                      One Time
                    </option>
                    <option key="Monthly" value="Monthly">
                      Monthly
                    </option>
                  </Field>
                </div>

                <div>
                  <ModalFieldLabel htmlFor="qtyOfLicenses">
                    Quantity
                  </ModalFieldLabel>
                  <Field
                    type="number"
                    id="quantity"
                    name="quantity"
                    className="frg-input-field color-light"
                    onWheel={handleWheel}
                    onChange={e => {
                      const { value } = e.target;
                      // Check if value contains a decimal point
                      if (!value.includes('.')) {
                        setFieldValue('quantity', value);
                        setErrors(prevErrors => ({
                          ...prevErrors,
                          quantity:
                            value > 0 ? '' : 'Quantity must be greater than 0'
                        }));
                      }
                    }}
                    style={{ width: '100%' }}
                    required
                  />
                </div>
                {errors && errors.quantity && (
                  <div style={{ color: 'red' }}>{errors.quantity}</div>
                )}

                <div>
                  <ModalFieldLabel htmlFor="cost">Unit Cost:</ModalFieldLabel>
                  <Field
                    type="number"
                    id="cost"
                    name="cost"
                    className="frg-input-field color-light"
                    onWheel={handleWheel}
                    onChange={e => {
                      const { value } = e.target;
                      setFieldValue('cost', e.target.value);
                      setErrors(prevErrors => ({
                        ...prevErrors,
                        cost:
                          value >= 0
                            ? ''
                            : 'Cost must be equal and greater than 0'
                      }));
                    }}
                    style={{ width: '100%' }}
                    required
                  />
                </div>
                {errors && errors.cost && (
                  <div style={{ color: 'red' }}>{errors.cost}</div>
                )}

                <div>
                  <ModalFieldLabel htmlFor="price">Unit Price:</ModalFieldLabel>
                  <Field
                    type="number"
                    id="price"
                    name="price"
                    className="frg-input-field color-light"
                    onWheel={handleWheel}
                    onChange={e => {
                      const { value } = e.target;
                      setFieldValue('price', e.target.value);
                      setErrors(prevErrors => ({
                        ...prevErrors,
                        price:
                          value >= 0
                            ? ''
                            : 'Price must be equal and greater than 0'
                      }));
                    }}
                    style={{ width: '100%' }}
                    required
                  />
                </div>
                {errors && errors.price && (
                  <div style={{ color: 'red' }}>{errors.price}</div>
                )}
                <div>
                  <ModalFieldLabel htmlFor="totalCost">
                    Total Cost:
                  </ModalFieldLabel>
                  <Field
                    type="number"
                    id="totalCost"
                    name="totalCost"
                    className="frg-input-field color-light"
                    value={values.totalCost.toFixed(2)}
                    disabled
                    onWheel={handleWheel}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <ModalFieldLabel htmlFor="totalPrice">
                    Total Price:
                  </ModalFieldLabel>
                  <Field
                    type="number"
                    id="totalPrice"
                    name="totalPrice"
                    className="frg-input-field color-light"
                    value={values.totalPrice.toFixed(2)}
                    disabled
                    onWheel={handleWheel}
                    style={{ width: '100%' }}
                  />
                </div>
                <ModalFooter>
                  <Button type="submit">Add</Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </div>
  );
};

PricingAndEstimates.propTypes = {
  onChange: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({}).isRequired,
  orderData: PropTypes.shape({}).isRequired,
  isEditable: PropTypes.bool
};

PricingAndEstimates.defaultProps = {
  isEditable: true // Set the default value for isEditable
};

export default PricingAndEstimates;
